import React, { useEffect, useState, useCallback, useMemo } from "react";
import homeBanner from "../../assets/homeBannner.png";
import mainstreamLogo from "../../assets/mainStream.png";
import { useStores } from "../../stores";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiGet, apiPostPut, apiGetFactFinder } from "../../api/api_methods";
import Loader from "../reusableComponents/loader";
import { observer } from "mobx-react-lite";
import ActionButton from "../reusableComponents/actionButton";
import { rolesWithId } from "../../constants/constants";
import { FaArrowLeftLong } from "react-icons/fa6";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import ReactPaginate from "react-paginate";
import adminAuthService from "../../services/authService";

const ClientsList = observer(() => {
  const navigate = useNavigate();
  const { clientsStore } = useStores();
  const [assignAgent, setAssignAgent] = useState({ agentId: "", formId: "" });
  const [reassignAgent, setReassignAgent] = useState({
    newAgentId: "",
    formId: "",
    previousAgentId: "",
    formData: null,
  });
  const [showReassignConfirm, setShowReassignConfirm] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);
  const [searchParams] = useSearchParams();
  const planTypeParam = searchParams.get("planType");
  const planType = clientsStore.normalizePlanType(planTypeParam);

  // Validate planType exists
  useEffect(() => {
    if (!planTypeParam || !planType) {
      console.error("PlanType is missing from URL:", planTypeParam);
      alert("Invalid plan type. Redirecting to dashboard...");
      navigate("/dashboard");
    }
  }, [planTypeParam, planType, navigate]);

  const [load, setLoad] = useState(false);
  const [tab, setTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(0); // ReactPaginate uses 0-based index
  const itemsPerPage = 10; // Define items per page
  // clientsApiCalled was used to track API calls but is no longer needed
  // eslint-disable-next-line no-unused-vars
  const [clientsApiCalled, setClientsApiCalled] = useState(false);

  const limit = itemsPerPage;
  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
    // Reset expanded accordion when page changes
    if (tab === 7) {
      setExpandedClientId(null);
    }
  };

  // count state is set but not directly used in rendering (status.count is used instead)
  // eslint-disable-next-line no-unused-vars
  const [count, setCount] = useState(0);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [currentPageData, setCurrentPageData] = useState([]); // Store current page's data
  const [totalPages, setTotalPages] = useState(0); // Store total pages from API

  // Fact Finder clients state
  const [factFinderClients, setFactFinderClients] = useState([]);
  const [factFinderSearch, setFactFinderSearch] = useState("");
  const [factFinderTotalPages, setFactFinderTotalPages] = useState(0);
  const [factFinderFilteredAll, setFactFinderFilteredAll] = useState([]); // Store all filtered clients for proper pagination
  // Track which client accordion is expanded
  const [expandedClientId, setExpandedClientId] = useState(null);
  // Search for Awaken plans (all tabs except Fact Finder)
  const [planSearch, setPlanSearch] = useState("");

  // Modal state for creating awaken form
  const [showCreateFormModal, setShowCreateFormModal] = useState(false);
  const [selectedClientData, setSelectedClientData] = useState(null); // Contains row and profile data
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [usedUIds, setUsedUIds] = useState(new Set()); // Track UIDs that already have forms
  const [checkingUIds, setCheckingUIds] = useState(false);

  // Find a richer copy of the client from any cache (page data, fact finder, store buckets)
  const findClientInStore = useCallback(
    (id) => {
      if (!id) return null;

      // 1) Current page data
      const currentMatch = (currentPageData || []).find((c) => c?._id === id);
      if (currentMatch) return currentMatch;

      // 2) Fact finder cache
      const factFinderMatch = (factFinderClients || []).find(
        (c) => c?._id === id || c?.userId === id
      );
      if (factFinderMatch) return factFinderMatch;

      // 3) Store buckets
      const buckets = clientsStore[planType]?.data || {};
      const lists = [
        buckets.all,
        buckets.totalAssigned,
        buckets.submittedForApproval,
        buckets.adminApproved,
        buckets.sharedWithClient,
        buckets.locked,
      ].filter(Array.isArray);

      for (const list of lists) {
        const match = list.find((c) => c?._id === id);
        if (match) return match;
      }

      return null;
    },
    [clientsStore, planType, currentPageData, factFinderClients]
  );

  // Build a plain snapshot of the row for emails to avoid missing fields
  const buildReassignSnapshot = useCallback(
    (row) => {
      if (!row) return null;
      const full = findClientInStore(row._id) || row;
      const personal =
        full.awareness?.personalDetails || full.personalDetails || {};
      const plan = full.awareness?.planDetails || full.planDetails || {};

      return {
        _id: full._id,
        factFinderId:
          full.factFinderId || full.factFinderUId || full.uId || full.uid,
        awareness: {
          personalDetails: {
            name:
              personal.name ||
              personal.fullName ||
              full.fullName ||
              full.name ||
              "Client",
            email:
              personal.email ||
              personal.officialEmail ||
              full.email ||
              "",
            phone:
              personal.phone ||
              personal.mobile ||
              personal.mobileNumber ||
              full.phone ||
              full.mobile ||
              "",
            dob: personal.dob || "",
            pan: personal.pan || full.pan || full.panNumber || "",
          },
          planDetails: {
            planType: plan.planType || full.planType || "",
            planYear: plan.planYear || full.planYear || "",
            planDuration: plan.planDuration || full.planDuration || "",
          },
        },
        assignedTo: full.assignedTo,
        createdAt: full.createdAt || full.createdOn || "",
        planType: plan.planType || full.planType || "",
      };
    },
    [findClientInStore]
  );

  // Function to store assignment intent when "Create Awaken Form" is clicked
  const storeAssignmentIntent = useCallback((uId) => {
    const currentUser = clientsStore.user;
    if (!uId || !currentUser?._id) return;

    const pendingAssignments = JSON.parse(localStorage.getItem('pendingFactFinderAssignments') || '{}');
    pendingAssignments[uId] = {
      agentId: currentUser._id,
      agentName: currentUser.name || currentUser.email,
      timestamp: Date.now()
    };
    localStorage.setItem('pendingFactFinderAssignments', JSON.stringify(pendingAssignments));
    console.log(`Assignment intent stored for UID: ${uId}, Agent: ${currentUser.name || currentUser.email}`);
  }, [clientsStore.user]);

  // tab with status
  const statuses = useMemo(
    () => ({
      1: "",
      2: "totalAssigned",
      3: "submittedForApproval",
      4: "adminApproved",
      5: "sharedWithClient",
      6: "locked",
    }),
    []
  );

  const statuses1 = useMemo(() => {
    console.log("statuses1 - planType:", planType);
    console.log("statuses1 - clientsStore:", clientsStore);
    console.log("statuses1 - clientsStore[planType]:", clientsStore[planType]);
    console.log(
      "statuses1 - clientMetrics:",
      clientsStore[planType]?.clientMetrics
    );

    const metrics = clientsStore[planType]?.clientMetrics || {};
    console.log("statuses1 - metrics:", metrics);
    console.log("statuses1 - metrics keys:", Object.keys(metrics));
    console.log("statuses1 - metrics values:", Object.values(metrics));

    const statuses = [
      {
        id: 1,
        name: "All Awaken",
        action: () => {
          setTab(1);
        },
        count:
          (metrics.totalAssigned || 0) +
          (metrics.submittedForApproval || 0) +
          (metrics.adminApproved || 0) +
          (metrics.sharedWithClient || 0) +
          (metrics.locked || 0),
      },
      {
        id: 2,
        name: "Total Assigned",
        action: () => {
          setTab(2);
        },
        count: metrics.totalAssigned || 0,
      },
      {
        id: 3,
        name: "Submitted for Approval",
        action: () => {
          setTab(3);
        },
        count: metrics.submittedForApproval || 0,
      },
      {
        id: 4,
        name: "Approved by Super Financial Planner",
        action: () => {
          setTab(4);
        },
        count: metrics.adminApproved || 0,
      },
      {
        id: 5,
        name: "Shared with Client",
        action: () => {
          setTab(5);
        },
        count: metrics.sharedWithClient || 0,
      },
      {
        id: 6,
        name: "Locked Awaken",
        action: () => {
          setTab(6);
        },
        count: metrics.locked || 0,
      },
      {
        id: 7,
        name: "Fact Finder Clients",
        action: () => {
          setTab(7);
        },
        count: 0, // Fact finder count will be managed separately
      },
    ];

    console.log("statuses1 - calculated statuses:", statuses);
    console.log("statuses1 - individual counts:", {
      totalAssigned: metrics.totalAssigned,
      submittedForApproval: metrics.submittedForApproval,
      adminApproved: metrics.adminApproved,
      sharedWithClient: metrics.sharedWithClient,
      locked: metrics.locked,
    });
    return statuses;
  }, [clientsStore, planType, forceUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAgents = useCallback(async () => {
    // If the current user is an agent, avoid calling the admin-only API.
    // Use the logged-in user as the sole selectable agent to prevent 403s.
    if (clientsStore?.user?.role === rolesWithId.agent) {
      if (!Array.isArray(clientsStore.agents) || clientsStore.agents.length === 0) {
        clientsStore.setAgents([clientsStore.user]);
      }
      return;
    }

    // Only fetch agents if they're not already loaded
    if (!Array.isArray(clientsStore.agents) || clientsStore.agents.length === 0) {
      try {
        const agentRoleId = "6720e9990ca50db671add43e";
        const apiResponse = await apiGet(`/users/get-users?role=${agentRoleId}`);
        if (apiResponse.status === 200) {
          // Fix: Extract the users array from the response
          const agentsData = apiResponse.body.data?.users || apiResponse.body.users || [];
          clientsStore.setAgents(agentsData);
        } else {
          console.error("Failed to fetch agents:", apiResponse?.message || apiResponse?.body?.message);
        }
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    }
  }, [clientsStore]);

  // Function to fetch existing forms and check for duplicate UIDs
  const checkUsedUIds = useCallback(async (uIds) => {
    if (!uIds || uIds.length === 0) return new Set();

    setCheckingUIds(true);
    const usedSet = new Set();

    try {
      // Fetch all clients across all plan types to check for existing UIDs
      const planTypes = ['comprehensive', 'term', 'health', 'pension', 'basic', 'zeroToOne', 'wealthManagement'];
      const user = clientsStore.user;

      for (const planTypeToCheck of planTypes) {
        try {
          let apiPath;
          if (user.role === rolesWithId.agent) {
            apiPath = `/adminPanel/get-clients?planType=${planTypeToCheck}&status=&agentId=${user._id}&page=1&limit=1000`;
          } else {
            apiPath = `/adminPanel/get-clients?planType=${planTypeToCheck}&status=&page=1&limit=1000`;
          }

          const response = await apiGet(apiPath);

          if (response.status === 200 && response.body.success) {
            const clients = response.body.data?.clients || response.body.data || [];

            clients.forEach(client => {
              // Check various UID fields
              const clientUId = client.factFinderId || client.factFinderUId || client.uId || client.uid;
              if (clientUId && uIds.includes(clientUId)) {
                usedSet.add(clientUId);
              }
            });
          }
        } catch (error) {
          console.error(`Error checking ${planTypeToCheck} forms:`, error);
        }
      }
    } catch (error) {
      console.error("Error checking used UIDs:", error);
    } finally {
      setCheckingUIds(false);
    }

    return usedSet;
  }, [clientsStore.user]);

  const fetchAllClients = useCallback(
    async (planType, status) => {
      try {
        setLoad(true);
        const user = clientsStore.user;
        let body;
        if (user.role === rolesWithId.agent) {
          body = { planType: planType, agentId: user._id };
        } else {
          body = { planType: planType, agentId: "" };
        }
        const apiResponse = await apiPostPut(
          body,
          "/adminPanel/get-10-clients-plan-type",
          "POST"
        );
        if (apiResponse.status === 200) {
          const data = apiResponse.body;
          console.log("fetchAllClients received data:", data);
          console.log("Client metrics from API:", data.clientMetrics);
          var allAwakensCount = 0;
          for (const key in data.clientMetrics) {
            allAwakensCount += data.clientMetrics[key];
          }
          const calculatedCount = status
            ? data.clientMetrics?.[planType]?.[status === "" ? "all" : status]
            : allAwakensCount;
          console.log("fetchAllClients - calculatedCount:", calculatedCount);
          setCount(calculatedCount);
          // Backend automatically filters out forms with renewals from locked awakens
          // when excludeRenewed=true (default), so no frontend filtering needed
          const allStatusData = {
            all: data.data.all,
            totalAssigned: data.data.totalAssigned,
            submittedForApproval: data.data.submittedForApproval,
            adminApproved: data.data.adminApproved,
            sharedWithClient: data.data.sharedWithClient,
            locked: data.data.locked, // Already filtered by backend
          };
          console.log(
            "fetchAllClients - storing data in store for planType:",
            planType
          );
          console.log(
            "fetchAllClients - data.clientMetrics:",
            data.clientMetrics
          );
          clientsStore.setClientsDataByStatus(
            allStatusData,
            data.clientMetrics,
            planType
          );
          console.log(
            "fetchAllClients - after storing, store data:",
            clientsStore[planType]?.clientMetrics
          );
          setClientsApiCalled(false);
          setLoad(false);
          // Force re-render to update counts
          setForceUpdate((prev) => prev + 1);
        } else {
          console.error("Failed to fetch clients data:", apiResponse?.message);
          alert("Failed to fetch clients data" + apiResponse?.message);
          setLoad(false);
        }
      } catch (error) {
        console.error("Error fetching clients data:", error);
        setLoad(false);
      }
    },
    [clientsStore]
  );

  // Function to refresh data after actions
  const refreshData = useCallback(async () => {
    console.log("Refreshing data after action...");
    try {
      // Refresh the main data (for metrics/counts)
      await fetchAllClients(planType);

      // Also refresh the current page data to show updated information
      // This will be handled by the useEffect that watches currentPage and tab
      // But we can trigger it explicitly here if needed

      // Force update to ensure UI reflects changes
      setForceUpdate((prev) => prev + 1);

      console.log("Data refreshed successfully");
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  }, [planType, fetchAllClients]);

  // Function to assign a client to an agent
  const assignClientToAgent = useCallback(async (formId, agentId, uId) => {
    try {
      const assignBody = {
        formId: formId,
        agentId: agentId,
      };

      const assignResponse = await apiPostPut(
        assignBody,
        "/adminPanel/assign-client",
        "PUT"
      );

      if (assignResponse.status === 200) {
        console.log(`Client with UID ${uId} automatically assigned to agent ${agentId}`);
        // Refresh data after assignment
        await refreshData();
        return true;
      } else {
        console.warn(`Failed to auto-assign client with UID ${uId}:`, assignResponse?.body?.message);
        return false;
      }
    } catch (error) {
      console.error(`Error auto-assigning client with UID ${uId}:`, error);
      return false;
    }
  }, [refreshData]);

  // Note: Assignment by UID endpoint doesn't exist yet
  // The assignment should happen automatically when the form is created
  // because we pass agentId in the form URL. The backend form creation endpoint
  // should handle the agentId parameter and auto-assign the client.

  // Fetch Fact Finder clients (profiles)
  const fetchFactFinderClients = useCallback(async () => {
    try {
      setLoad(true);
      const user = clientsStore.user;
      const searchQuery = factFinderSearch.trim();

      // Fetch ALL pages of data to properly filter and paginate on frontend
      // This ensures accurate pagination when filtering by plan type
      let allClients = [];
      let currentPageNum = 1;
      const maxPagesToFetch = 100; // Safety limit to prevent infinite loops

      // Build base API path
      let baseApiPath;
      if (searchQuery) {
        baseApiPath = `/Exports/GetClients?page=${currentPageNum}&pageSize=100&search=${encodeURIComponent(searchQuery)}`;
      } else {
        baseApiPath = `/Exports/GetClients?page=${currentPageNum}&pageSize=100`;
      }

      // If user is a financial planner (agent), add email filter
      const isFinancialPlanner = user.role === rolesWithId.agent;
      const userEmail = user.email?.toLowerCase();

      if (isFinancialPlanner && userEmail) {
        const separator = baseApiPath.includes('?') ? '&' : '?';
        baseApiPath += `${separator}email=${encodeURIComponent(userEmail)}`;
      }

      // Fetch all pages
      while (currentPageNum <= maxPagesToFetch) {
        const apiPath = baseApiPath.replace(/page=\d+/, `page=${currentPageNum}`);
        console.log(`Fetching Fact Finder page ${currentPageNum} from:`, apiPath);
        
        const apiResponse = await apiGetFactFinder(apiPath);
        
        if (apiResponse.status === 200 && apiResponse.body.success) {
          const clients = apiResponse.body.data || [];
          if (clients.length === 0) break; // No more data
          
          allClients = allClients.concat(clients);
          
          // If we got fewer results than page size, we've reached the last page
          if (clients.length < 100) break;
          
          currentPageNum++;
        } else {
          break; // Stop on error
        }
      }

      console.log(`Fetched ${allClients.length} total Fact Finder clients across ${currentPageNum} pages`);

      // Filter clients to only show those with profiles matching the current plan type
      // Also filter profiles within each client to only show matching plan types
      const filteredClients = allClients
        .map((client) => {
          const profiles = client.plans || [];
          // Filter profiles to only include those matching the current plan type
          const matchingProfiles = profiles.filter((profile) => {
            const profilePlanType = profile.plan || 
                                   profile.awareness?.planDetails?.planType || 
                                   profile.planType || 
                                   "";
            
            // Normalize both plan types for comparison
            const normalizedProfilePlan = clientsStore.normalizePlanType(profilePlanType);
            const normalizedCurrentPlan = clientsStore.normalizePlanType(planType);
            
            return normalizedProfilePlan === normalizedCurrentPlan;
          });
          
          // Only include client if they have at least one matching profile
          if (matchingProfiles.length > 0) {
            return {
              ...client,
              plans: matchingProfiles, // Replace plans array with filtered profiles
            };
          }
          return null; // Exclude clients with no matching profiles
        })
        .filter((client) => client !== null); // Remove null entries

      // Store all filtered clients for pagination
      setFactFinderFilteredAll(filteredClients);
      
      // Calculate proper total pages based on filtered results
      const filteredTotalPages = Math.ceil(filteredClients.length / itemsPerPage);
      
      // Now paginate the filtered results properly
      const startIndex = 0; // Always start from first page when fetching
      const endIndex = itemsPerPage;
      const paginatedClients = filteredClients.slice(startIndex, endIndex);
      
      setFactFinderClients(paginatedClients);
      setFactFinderTotalPages(filteredTotalPages);
      setLoad(false);
    } catch (error) {
      console.error("Error fetching fact finder profiles:", error);
      setFactFinderClients([]);
      setFactFinderTotalPages(0);
      setFactFinderFilteredAll([]);
      setLoad(false);
    }
  }, [factFinderSearch, itemsPerPage, planType, clientsStore]);

  // Handle pagination for already filtered Fact Finder clients
  useEffect(() => {
    if (tab === 7 && factFinderFilteredAll.length > 0) {
      const startIndex = currentPage * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedClients = factFinderFilteredAll.slice(startIndex, endIndex);
      
      setFactFinderClients(paginatedClients);
      const filteredTotalPages = Math.ceil(factFinderFilteredAll.length / itemsPerPage);
      setFactFinderTotalPages(filteredTotalPages);
    }
  }, [currentPage, factFinderFilteredAll, itemsPerPage, tab]);

  const fetchClientsData = useCallback(
    async (planType, status) => {
      try {
        // If tab is 7 (Fact Finder), use fact finder fetch instead
        if (tab === 7) {
          await fetchFactFinderClients();
          return;
        }

        if (!status) status = "";

        // Always use paginated API endpoint for fetching client data
        const user = clientsStore.user;
        let api;
        // Convert 0-based currentPage to 1-based page number for API
        const searchTerm = planSearch.trim();
        const pageNumber = searchTerm ? 1 : currentPage + 1;
        // When searching, fetch a large page so search can run across all results
        const effectiveLimit = searchTerm ? 1000 : limit;
        if (user.role === rolesWithId.agent) {
          api = `/adminPanel/get-clients?planType=${planType}&status=${status}&agentId=${user._id}&page=${pageNumber}&limit=${effectiveLimit}`;
        } else {
          api = `/adminPanel/get-clients?planType=${planType}&status=${status}&page=${pageNumber}&limit=${effectiveLimit}`;
        }
        setLoad(true);
        const apiResponse = await apiGet(api);
        if (apiResponse.status === 200) {
          // Extract pagination info from response
          const pagination = apiResponse.body.data?.pagination || apiResponse.body.pagination || {};
          const totalCount = pagination.total || apiResponse.body.count || 0;
          // When searching, show all results on a single page in the UI
          const totalPages = searchTerm
            ? 1
            : pagination.totalPages || Math.ceil(totalCount / effectiveLimit);

          // Store paginated data in local state for rendering
          // Backend automatically filters out forms with renewals when status=locked
          // (excludeRenewed defaults to true)
          const paginatedClients = apiResponse.body.data?.clients || apiResponse.body.data || [];
          
          setCount(totalCount);
          setCurrentPageData(paginatedClients);
          // Store totalPages in a state for pagination component
          setTotalPages(totalPages);
          // Also update store for other operations if needed
          clientsStore.setClientsData(
            apiResponse.body,
            planType,
            status === "" ? "all" : status
          );
          setClientsApiCalled(true);
          setLoad(false);
        } else {
          console.error("Failed to fetch clients data:", apiResponse?.message);
          alert("Failed to fetch clients data" + apiResponse?.message);
          setLoad(false);
        }
      } catch (error) {
        console.error("Error fetching clients data:", error);
        setLoad(false);
      }
    },
    [
      clientsStore,
      currentPage,
      tab,
      limit,
      fetchFactFinderClients,
      planSearch,
    ]
  );

  // Function to process pending assignments by checking for clients with matching UIDs
  const processPendingAssignments = useCallback(async () => {
    const pendingAssignments = JSON.parse(localStorage.getItem('pendingFactFinderAssignments') || '{}');
    if (Object.keys(pendingAssignments).length === 0) return;

    const currentUser = clientsStore.user;
    if (!currentUser?._id) return;

    const isAdmin = currentUser.role === rolesWithId.admin;
    const processedUIds = [];

    // For financial planners, check their assigned clients list (which they CAN access)
    if (!isAdmin) {
      console.log("Financial planner detected - checking assigned clients for pending assignments");
      console.log("Pending assignments:", Object.keys(pendingAssignments));
      try {
        // Fetch assigned clients for the financial planner
        // Check all plan types to find the client
        const planTypes = ['comprehensive', 'term', 'health', 'pension'];
        let allClients = [];

        for (const planTypeToCheck of planTypes) {
          const apiPath = `/adminPanel/get-clients?planType=${planTypeToCheck}&status=&agentId=${currentUser._id}&page=1&limit=100`;
          const response = await apiGet(apiPath);

          if (response.status === 200 && response.body.success) {
            const clients = response.body.data?.clients || response.body.data || [];
            allClients = allClients.concat(clients);
          }
        }

        console.log(`Found ${allClients.length} assigned clients across all plan types`);

        // Check each client for matching UID (use for...of to handle async properly)
        for (let index = 0; index < allClients.length; index++) {
          const client = allClients[index];
          // Log first few clients for debugging
          if (index < 3) {
            console.log(`Client ${index}:`, {
              _id: client._id,
              factFinderUId: client.factFinderUId,
              uId: client.uId,
              factFinderId: client.factFinderId,
              assignedTo: client.assignedTo?._id,
              name: client.name
            });
          }

          // Check various UID fields that might be stored
          // Backend saves uid as factFinderId, so check that first
          const clientUId = client.factFinderId ||  // Primary field (backend saves uid here)
            client.factFinderUId ||
            client.uId ||
            client.uid;

          if (clientUId && pendingAssignments[clientUId]) {
            const assignment = pendingAssignments[clientUId];

            // Check if client is already assigned to the correct agent
            if (client.assignedTo?._id === assignment.agentId) {
              // Already assigned correctly - remove from pending
              console.log(`✅ Found assigned client with UID ${clientUId} - already assigned to ${assignment.agentName}`);
              console.log(`Client details:`, {
                _id: client._id,
                name: client.name,
                assignedTo: client.assignedTo?._id,
                UID: clientUId,
                factFinderId: client.factFinderId
              });
              processedUIds.push(clientUId);
            } else if (!client.assignedTo?._id) {
              // Form created but not assigned yet - assign it now (like createnew.jsx does)
              console.log(`🔄 Form created but not assigned yet. Assigning now...`);
              console.log(`Client details:`, {
                _id: client._id,
                name: client.name,
                UID: clientUId,
                factFinderId: client.factFinderId
              });

              // Explicitly assign the client (same as createnew.jsx)
              const assigned = await assignClientToAgent(client._id, assignment.agentId, clientUId);
              if (assigned) {
                console.log(`✅ Successfully assigned client with UID ${clientUId} to ${assignment.agentName}`);
                processedUIds.push(clientUId);
              } else {
                console.warn(`⚠️ Failed to assign client with UID ${clientUId} - will retry later`);
                // Don't remove from pending, will retry next time
              }
            } else {
              // Assigned to different agent - log warning
              console.warn(`⚠️ Client with UID ${clientUId} is assigned to different agent:`, {
                expected: assignment.agentId,
                actual: client.assignedTo?._id,
                clientId: client._id
              });
              // Still remove from pending since it's assigned (just not to us)
              processedUIds.push(clientUId);
            }

            // Refresh the client list to show the newly assigned client
            if (processedUIds.includes(clientUId)) {
              setTimeout(() => {
                refreshData();
                fetchClientsData(planType, statuses[tab]);
              }, 1000);
            }

            // FALLBACK: Check if this is a newly created client that matches pending assignment
            // This handles cases where backend saves factFinderId but doesn't return it in API response
            if ((client.createdDate || client.createdAt) && Object.keys(pendingAssignments).length > 0 && !clientUId) {
              const createdTime = new Date(client.createdDate || client.createdAt).getTime();
              const threeMinutesAgo = Date.now() - (3 * 60 * 1000);

              if (createdTime > threeMinutesAgo) {
                const pendingUIds = Object.keys(pendingAssignments);
                for (const pendingUId of pendingUIds) {
                  const assignment = pendingAssignments[pendingUId];

                  // If client is assigned to the agent we're expecting AND was created very recently
                  // AND doesn't have factFinderId (backend bug), assume it's our match
                  if (client.assignedTo?._id === assignment.agentId && !client.factFinderId) {
                    const secondsAgo = Math.round((Date.now() - createdTime) / 1000);
                    const twoMinutesAgo = Date.now() - (2 * 60 * 1000);

                    console.log(`🔍 FALLBACK MATCH: Found recently created client that might match UID ${pendingUId}:`, {
                      _id: client._id,
                      name: client.name,
                      createdSecondsAgo: secondsAgo,
                      assignedTo: client.assignedTo?._id,
                      expectedAgent: assignment.agentId,
                      factFinderId: client.factFinderId
                    });

                    if (createdTime > twoMinutesAgo) {
                      console.log(`✅ Treating as match (created ${secondsAgo}s ago, assigned correctly, factFinderId missing)`);
                      console.log(`⚠️ This is a workaround - backend should return factFinderId in API response`);
                      processedUIds.push(pendingUId);
                      setTimeout(() => {
                        refreshData();
                        fetchClientsData(planType, statuses[tab]);
                      }, 1000);
                      break;
                    }
                  }
                }
              }
            }
          }
        }

        if (processedUIds.length === 0 && Object.keys(pendingAssignments).length > 0) {
          console.log("⚠️ No matching clients found yet. Pending UIDs:", Object.keys(pendingAssignments));
          console.log("");
          console.log("🔍 DEBUGGING CHECKLIST:");
          console.log("1. Check if form was created (look for clients created within last 3 minutes)");
          console.log("2. Check backend: db.forms.findOne({ factFinderId: 'MSOW89NSBS' })");
          console.log("3. Check API projection: /adminPanel/get-clients should include factFinderId");
          console.log("");
          console.log("🔧 BACKEND FIX REQUIRED:");
          console.log("The backend MUST include factFinderId in /adminPanel/get-clients projection");
        }
      } catch (error) {
        console.error("Error checking assigned clients for financial planner:", error);
      }

      // Remove processed assignments
      if (processedUIds.length > 0) {
        processedUIds.forEach(uId => {
          delete pendingAssignments[uId];
        });
        localStorage.setItem('pendingFactFinderAssignments', JSON.stringify(pendingAssignments));
        console.log(`Processed ${processedUIds.length} pending assignments for financial planner`);
      }

      return;
    }

    // For admins, fetch all clients and check for matches
    try {
      const pageSize = 100;
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const apiPath = `/adminPanel/clients?page=${page}&limit=${pageSize}`;
        const response = await apiGet(apiPath);

        if (response.status === 200 && response.body.success) {
          const clients = response.body.data?.clients || response.body.data || [];

          // Check each client for matching UID
          clients.forEach(client => {
            if (client.factFinderUId || client.uId) {
              const uId = client.factFinderUId || client.uId;
              if (pendingAssignments[uId] && !client.assignedTo?._id) {
                // Found a match - assign the client
                const assignment = pendingAssignments[uId];
                assignClientToAgent(client._id, assignment.agentId, uId);
                processedUIds.push(uId);
              }
            }
          });

          // Check if there are more pages
          const pagination = response.body.data?.pagination || response.body.pagination || {};
          const totalPages = pagination.totalPages || Math.ceil((pagination.total || 0) / pageSize);
          hasMore = page < totalPages;
          page++;
        } else {
          hasMore = false;
        }
      }
    } catch (error) {
      console.error("Error processing pending assignments:", error);
    }

    // Remove processed assignments
    if (processedUIds.length > 0) {
      processedUIds.forEach(uId => {
        delete pendingAssignments[uId];
      });
      localStorage.setItem('pendingFactFinderAssignments', JSON.stringify(pendingAssignments));
      console.log(`Processed ${processedUIds.length} pending assignments`);
    }
  }, [clientsStore.user, assignClientToAgent, refreshData, fetchClientsData, planType, statuses, tab]);

  // Function to refresh form data specifically
  const refreshFormData = useCallback(async () => {
    console.log("Refreshing form data after interaction...");
    try {
      // Refresh the current tab data to get updated form information
      await fetchClientsData(planType, statuses[tab]);

      // Force update to ensure UI reflects changes
      setForceUpdate((prev) => prev + 1);

      console.log("Form data refreshed successfully");
    } catch (error) {
      console.error("Error refreshing form data:", error);
    }
  }, [planType, fetchClientsData, statuses, tab]);

  // Refresh data when page changes
  useEffect(() => {
    if (tab === 7) {
      fetchFactFinderClients();
    } else {
      fetchClientsData(planType, statuses[tab]);
    }
  }, [currentPage, planType, statuses, tab, fetchClientsData, fetchFactFinderClients]);

  // Reset to first page when search changes
  useEffect(() => {
    if (tab === 7 && factFinderSearch !== undefined) {
      setCurrentPage(0);
      setExpandedClientId(null);
    }
  }, [factFinderSearch, tab]);

  useEffect(() => {
    if (tab !== 7) {
      setCurrentPage(0);
      setExpandedClientId(null);
    }
  }, [planSearch, tab]);

  useEffect(() => {
    // Don't fetch if planType is invalid
    if (!planType || planType === "") {
      console.warn("PlanType is empty, skipping data fetch");
      return;
    }

    const initializeData = async () => {
      await fetchAllClients(planType);
      await fetchAgents();
      // Force update to ensure counts are displayed
      setForceUpdate((prev) => prev + 1);
    };
    initializeData();
  }, [planType, fetchAllClients, fetchAgents]);

  // Process pending assignments when component mounts or when clients are fetched
  useEffect(() => {
    // Process pending assignments after a short delay to allow for any new clients to be created
    const timer = setTimeout(() => {
      processPendingAssignments();
    }, 2000); // 2 second delay

    return () => clearTimeout(timer);
  }, [currentPageData, processPendingAssignments]);

  // Check for used UIDs when modal opens or when factFinder clients are loaded
  useEffect(() => {
    if (tab === 7 && factFinderClients.length > 0) {
      // Extract all UIDs from factFinder clients
      const allUIds = [];
      factFinderClients.forEach(row => {
        const profiles = row.plans || [];
        profiles.forEach(profile => {
          if (profile.uId && profile.uId.startsWith("MS")) {
            allUIds.push(profile.uId);
          }
        });
      });

      if (allUIds.length > 0) {
        checkUsedUIds(allUIds).then(usedSet => {
          setUsedUIds(usedSet);
        });
      }
    }
  }, [tab, factFinderClients, checkUsedUIds]);

  // Check for used UID when modal opens with specific profile
  useEffect(() => {
    if (showCreateFormModal && selectedClientData?.profile?.uId) {
      checkUsedUIds([selectedClientData.profile.uId]).then(usedSet => {
        setUsedUIds(prev => {
          const newSet = new Set(prev);
          usedSet.forEach(uId => newSet.add(uId));
          return newSet;
        });
      });
    }
  }, [showCreateFormModal, selectedClientData, checkUsedUIds]);

  useEffect(() => {
    // Reset to first page when tab changes
    setCurrentPage(0);
    setCurrentPageData([]); // Clear current page data when tab changes
    setTotalPages(0); // Reset total pages when tab changes
    // Clear fact finder data when switching away from tab 7
    if (tab !== 7) {
      setFactFinderClients([]);
      setFactFinderSearch("");
      setFactFinderTotalPages(0);
    }
  }, [tab]);

  useEffect(() => {
    // Handle Fact Finder tab separately
    if (tab === 7) {
      fetchFactFinderClients();
      return;
    }

    statuses1.forEach((status) => {
      if (status.id === tab) {
        if (status.count === 0) {
          setCount(0);
          setCurrentPageData([]); // Clear current page data
          setTotalPages(0); // Reset total pages
          // Clear the data for this status
          clientsStore.setClientsData(
            { data: [], count: 0 },
            planType,
            statuses[tab] === "" ? "all" : statuses[tab]
          );
        }
      }
    });
    // Remove currentPage from dependencies to avoid conflicts with pagination
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planType, tab, statuses, statuses1, clientsStore, fetchFactFinderClients]);

  // Add a refresh mechanism when tab changes
  useEffect(() => {
    const refreshOnTabChange = async () => {
      if (tab !== 1) {
        // Don't refresh on "All Awaken" tab to avoid double loading
        await refreshData();
      }
    };
    refreshOnTabChange();
  }, [tab, refreshData]);

  // useEffect(() => {
  //   setCurrentPage(0);
  //   // fetchClientsData(planType, statuses[tab]);
  // }, [tab]);

  // Function to send assignment notification email to financial planner
  const sendAssignmentNotification = async (agentId, formData) => {
    try {
      // Find the agent details from the agents list
      const agent = Array.isArray(clientsStore.agents)
        ? clientsStore.agents.find((agent) => agent._id === agentId)
        : null;

      if (!agent || !agent.email) {
        console.warn("No agent email found for assignment notification");
        return;
      }

      const subject = "New Client Assignment Notification - Awaken - Financial Journey";

      const emailBody = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #2563eb;">New Client Assignment</h2>
          <p>Dear ${agent.name || "Financial Planner"},</p>
          <p>You have been assigned a new client for financial planning.</p>
          
          <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #374151; margin-top: 0;">Client Details:</h3>
            <p><strong>Client Name:</strong> ${formData.awareness?.personalDetails?.name ||
        formData.name ||
        "N/A"
        }</p>
            <p><strong>Client Mobile Number:</strong> ${formData.awareness?.personalDetails?.phone || "N/A"}</p>
            <p><strong>Client Email:</strong> ${formData.awareness?.personalDetails?.email || "N/A"}</p>
            <p><strong>Plan Type:</strong> ${formData.awareness?.planDetails?.planType || "N/A"}</p>
            <p><strong>Created Date:</strong> ${new Date(
          formData.createdAt || formData.createdOn
        ).toLocaleDateString()}</p>
            <p><strong>Status:</strong> Assigned</p>
          </div>
          
          <p>Please log in to your dashboard to view the client details and begin the financial planning process.</p>
          
          <p>Best regards,<br>Awaken Admin Team</p>
        </div>
      `;

      // Send the email
      const emailData = {
        to: [agent.email],
        subject: subject,
        html: emailBody,
        from: "Awaken - Financial Journey <manoj@mainstream.co.in>",
      };

      const response = await apiPostPut(
        emailData,
        "/adminPanel/send-email",
        "POST"
      );

      if (response.status === 200) {
        console.log("Assignment notification sent successfully");
      } else {
        console.error(
          "Failed to send assignment notification:",
          response.body?.message
        );
      }
    } catch (error) {
      console.error("Error sending assignment notification:", error);
    }
  };

  // Function to send reassignment notification emails
  const sendReassignmentNotification = async (
    newAgentId,
    previousAgentId,
    formData
  ) => {
    try {
      // Utility to avoid showing "N/A"
      const displayOrDash = (value) => {
        if (value === 0) return "0";
        return value ? value : "-";
      };

      // Find the new and previous agent details
      const newAgent = Array.isArray(clientsStore.agents)
        ? clientsStore.agents.find((agent) => agent._id === newAgentId)
        : null;
      const previousAgent = Array.isArray(clientsStore.agents)
        ? clientsStore.agents.find((agent) => agent._id === previousAgentId)
        : null;

      // Common details
      const uid =
        formData.factFinderId ||
        formData.factFinderUId ||
        formData.uId ||
        formData.uid ||
        formData.formId ||
        formData._id ||
        "-";
      const clientName =
        formData.awareness?.personalDetails?.name ||
        formData.awareness?.personalDetails?.fullName ||
        formData.fullName ||
        formData.name ||
        "-";
      const clientEmail =
        formData.awareness?.personalDetails?.email ||
        formData.awareness?.personalDetails?.officialEmail ||
        formData.email ||
        "-";
      const clientPhone =
        formData.awareness?.personalDetails?.phone ||
        formData.awareness?.personalDetails?.mobile ||
        formData.awareness?.personalDetails?.mobileNumber ||
        formData.phone ||
        "-";
      const clientPan =
        formData.awareness?.personalDetails?.pan ||
        formData.pan ||
        formData.panNumber ||
        "";
      const planType =
        formData.awareness?.planDetails?.planType ||
        formData.planType ||
        "-";
      const planYear =
        formData.awareness?.planDetails?.planYear ||
        formData.planYear ||
        "-";
      const planDuration =
        formData.awareness?.planDetails?.planDuration ||
        formData.planDuration ||
        "-";
      const createdDate = formData.createdAt || formData.createdOn;
      const createdDateDisplay = createdDate
        ? new Date(createdDate).toLocaleDateString()
        : "-";
      const changedByDisplayName =
        clientsStore.user?.displayName ||
        clientsStore.user?.name ||
        clientsStore.user?.email ||
        "Admin";
      const reassignmentReason = formData.reason || "Client reassignment by admin";

      // Send notification to new agent
      if (newAgent && newAgent.email) {
        const newAgentSubject =
          "Client Reassignment - New Assignment - Awaken - Financial Journey";
        const newAgentEmailBody = `
          <div style="font-family: 'Segoe UI', Arial, sans-serif; background-color: #f5f7fb; padding: 24px;">
            <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 640px; margin: 0 auto; background: #ffffff; border-radius: 12px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06); overflow: hidden;">
              <tr>
                <td style="padding: 20px 24px 14px; text-align: center; border-bottom: 1px solid #e5e7eb;">
                  <div style="display: inline-block; padding: 10px 16px; background: #111827; color: #ffffff; border-radius: 999px; font-weight: 600; letter-spacing: 0.2px;">Awaken - Financial Journey</div>
                  <h2 style="margin: 14px 0 6px; color: #111827; font-size: 20px;">Client reassigned to you</h2>
                  <p style="margin: 0; color: #4b5563; font-size: 14px;">You have been assigned a client previously handled by another planner.</p>
                </td>
              </tr>
              <tr>
                <td style="padding: 18px 24px 8px;">
                  <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse;">
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px; width: 36%;">UID</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(uid)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Client Name</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientName)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Client Email</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientEmail)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Client Mobile</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientPhone)}</td>
                    </tr>
                    ${
                      clientPan
                        ? `<tr>
                            <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Client PAN</td>
                            <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientPan)}</td>
                          </tr>`
                        : ""
                    }
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Plan Type</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(planType)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Plan Year</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(planYear)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Plan Duration</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(planDuration)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Created Date</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(createdDateDisplay)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Previous Agent</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(previousAgent?.name || previousAgent?.displayName || previousAgent?.fullName)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">New Status</td>
                      <td style="padding: 10px 0; color: #059669; font-size: 14px; font-weight: 700;">Reassigned to You</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Reason</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(reassignmentReason)}</td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr>
                <td style="padding: 0 24px 20px; text-align: center; color: #6b7280; font-size: 13px;">
                  <p style="margin: 0 0 6px;">Please log in to the dashboard to continue planning.</p>
                  <p style="margin: 0; color: #111827; font-weight: 600;">Regards,<br>${displayOrDash(changedByDisplayName)}<br>Awaken - Financial Journey</p>
                </td>
              </tr>
            </table>
          </div>
        `;

        const newAgentEmailData = {
          to: [newAgent.email],
          subject: newAgentSubject,
          html: newAgentEmailBody,
          from: `"${changedByDisplayName} (Awaken)" <awaken@mainstream.co.in>`,
        };

        await apiPostPut(newAgentEmailData, "/adminPanel/send-email", "POST");
      }

      // Send notification to previous agent
      if (previousAgent && previousAgent.email) {
        const previousAgentSubject =
          "Client Reassignment - Assignment Removed - Awaken - Financial Journey";
        const previousAgentEmailBody = `
          <div style="font-family: 'Segoe UI', Arial, sans-serif; background-color: #f5f7fb; padding: 24px;">
            <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 640px; margin: 0 auto; background: #ffffff; border-radius: 12px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06); overflow: hidden;">
              <tr>
                <td style="padding: 20px 24px 14px; text-align: center; border-bottom: 1px solid #e5e7eb;">
                  <div style="display: inline-block; padding: 10px 16px; background: #111827; color: #ffffff; border-radius: 999px; font-weight: 600; letter-spacing: 0.2px;">Awaken - Financial Journey</div>
                  <h2 style="margin: 14px 0 6px; color: #b91c1c; font-size: 20px;">Client reassigned from you</h2>
                  <p style="margin: 0; color: #4b5563; font-size: 14px;">This client has been reassigned to another planner.</p>
                </td>
              </tr>
              <tr>
                <td style="padding: 18px 24px 8px;">
                  <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse;">
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px; width: 36%;">UID</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(uid)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Client Name</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientName)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Client Email</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientEmail)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Client Mobile</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientPhone)}</td>
                    </tr>
                    ${
                      clientPan
                        ? `<tr>
                            <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Client PAN</td>
                            <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientPan)}</td>
                          </tr>`
                        : ""
                    }
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Plan Type</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(planType)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Plan Year</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(planYear)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Plan Duration</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(planDuration)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Created Date</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(createdDateDisplay)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">New Agent</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(newAgent?.name || newAgent?.displayName || newAgent?.fullName)}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">New Status</td>
                      <td style="padding: 10px 0; color: #b91c1c; font-size: 14px; font-weight: 700;">Reassigned to Another Planner</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Reason</td>
                      <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(reassignmentReason)}</td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr>
                <td style="padding: 0 24px 20px; text-align: center; color: #6b7280; font-size: 13px;">
                  <p style="margin: 0 0 6px;">This client is no longer under your responsibility.</p>
                  <p style="margin: 0; color: #111827; font-weight: 600;">Regards,<br>${displayOrDash(changedByDisplayName)}<br>Awaken - Financial Journey</p>
                </td>
              </tr>
            </table>
          </div>
        `;

        const previousAgentEmailData = {
          to: [previousAgent.email],
          subject: previousAgentSubject,
          html: previousAgentEmailBody,
          from: `"${changedByDisplayName} (Awaken)" <awaken@mainstream.co.in>`,
        };

        await apiPostPut(
          previousAgentEmailData,
          "/adminPanel/send-email",
          "POST"
        );
      }

      console.log("Reassignment notifications sent successfully");
    } catch (error) {
      console.error("Error sending reassignment notifications:", error);
    }
  };

  // Function to handle reassignment
  const handleReassignment = async () => {
    try {
      console.log("Starting reassignment, setting load to true");
      setIsReassigning(true);
      setLoad(true);
      const body = {
        formId: reassignAgent.formId,
        newAgentId: reassignAgent.newAgentId,
        reason: "Client reassignment by admin",
        skipEmail: true, // Frontend handles email sending to avoid duplicates
      };

      const apiResponse = await apiPostPut(
        body,
        "/adminPanel/reassign-client",
        "PUT"
      );

      console.log("Reassignment API response:", apiResponse);

      if (apiResponse.status === 200 && apiResponse.body?.success) {
        console.log(
          "Reassignment successful, response data:",
          apiResponse.body.data
        );

        // Get agent information from the response
        const responseData = apiResponse.body.data;
        const previousAgentId = responseData.previousAgent?._id;
        const newAgentId = responseData.newAgent?._id;

        // Use the current row data when available to populate email details
        const snapshot =
          reassignAgent.formData ||
          (reassignAgent.formData?._id
            ? buildReassignSnapshot(reassignAgent.formData)
            : null) ||
          buildReassignSnapshot({ _id: responseData.formId });

        const formData = {
          ...(snapshot || {}),
          _id: responseData.formId || snapshot?._id,
          assignedTo: responseData.previousAgent || snapshot?.assignedTo,
          awareness: snapshot?.awareness || { personalDetails: { name: "Client" } },
          planType:
            snapshot?.awareness?.planDetails?.planType ||
            snapshot?.planType ||
            planType,
          createdAt:
            snapshot?.createdAt ||
            snapshot?.createdOn ||
            responseData?.createdAt ||
            new Date().toISOString(),
          reason: body.reason,
        };

        // Send reassignment notifications only if backend didn't send them
        // Check if backend response indicates emails were already sent
        const emailsAlreadySent = apiResponse.body?.data?.emailsSent === true || 
                                  apiResponse.body?.emailsSent === true;
        
        if (!emailsAlreadySent) {
          try {
            await sendReassignmentNotification(
              newAgentId,
              previousAgentId,
              formData
            );
          } catch (emailError) {
            console.error("Error sending email notifications:", emailError);
            // Don't fail the reassignment if email fails
          }
        } else {
          console.log("Backend already sent reassignment emails, skipping frontend email sending");
        }

        // Reset state and close modal
        setReassignAgent({ newAgentId: "", formId: "", previousAgentId: "", formData: null });
        setShowReassignConfirm(false);
        setIsReassigning(false);
        setLoad(false);

        // Show success message
        alert(apiResponse.body.message || "Client reassigned successfully");

        // Refresh the data to update counts and lists
        await refreshData();

        // Also refresh form data to show updated reassignment
        await refreshFormData();
      } else {
        setLoad(false);
        setIsReassigning(false);
        const errorMessage =
          apiResponse?.body?.message ||
          apiResponse?.message ||
          "Unknown error occurred";
        alert("Failed to reassign client: " + errorMessage);
        console.error("Failed to reassign client:", apiResponse);
      }
    } catch (error) {
      setLoad(false);
      setIsReassigning(false);
      alert("Error reassigning client");
      console.error("Error reassigning client:", error);
    }
  };

  const renderTableData = () => {
    // Handle Fact Finder clients (tab 7)
    if (tab === 7) {
      const dataToRender = factFinderClients || [];

      if (dataToRender.length === 0) {
        return (
          <tr>
            <td colSpan={6} className="text-center py-4 text-gray-500">
              No Data Found
            </td>
          </tr>
        );
      }

      return dataToRender.map((row, index) => {
        const globalIndex = currentPage * itemsPerPage + index + 1;
        // Use a unique identifier - prefer userId (from API), then id, fallback to index
        const clientId = (row.userId !== undefined && row.userId !== null)
          ? `client-${row.userId}`
          : (row.id !== undefined && row.id !== null)
            ? `client-${row.id}`
            : `client-index-${index}`;
        const isExpanded = expandedClientId === clientId;
        const profiles = row.plans || [];

        return (
          <React.Fragment key={clientId}>
            {/* Main client row */}
            <tr className="border-b bg-gray-50 hover:bg-gray-100">
              <td className="text-center py-3 w-20">{globalIndex}.</td>
              <td className="text-left pl-8 py-3 w-20">
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setExpandedClientId(isExpanded ? null : clientId);
                    }}
                    className="text-gray-600 hover:text-primary"
                  >
                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  <span className="font-medium">{row.name || "N/A"}</span>
                </div>
              </td>
              <td className="text-left pl-8 py-3 w-20">{row.advisorName || "N/A"}</td>
              <td className="text-center py-3 w-20">
                {row.createdDate ? clientsStore.setFormattedDate(row.createdDate) : "N/A"}
              </td>
              <td className="text-center py-3 w-20">
                <span className={`px-2 py-1 rounded text-xs ${row.status === "Active"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
                  }`}>
                  {row.status || "N/A"}
                </span>
              </td>
              <td className="text-center py-3 w-20">
                <span className="text-xs text-gray-600">
                  {profiles.length} {profiles.length === 1 ? 'Profile' : 'Profiles'}
                </span>
              </td>
            </tr>
            {/* Expanded profiles section */}
            {isExpanded && profiles.length > 0 && (
              <tr className="border-b">
                <td colSpan={6} className="px-4 py-4 bg-white">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-700 mb-3">Profiles:</h4>
                    <div className="space-y-2">
                      {profiles.map((profile, profileIndex) => (
                        <div
                          key={profile.profileId || profileIndex}
                          className="border rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                            <div>
                              <span className="text-xs text-gray-500">UID:</span>
                              <p className="font-mono text-sm font-semibold">{profile.uId || "N/A"}</p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Plan:</span>
                              <p className="text-sm">
                                {profile.awareness?.planDetails?.planType || profile.plan || "N/A"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Plan Year:</span>
                              <p className="text-sm">
                                {profile.awareness?.planDetails?.planYear || profile.planYear || "N/A"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Plan Duration:</span>
                              <p className="text-sm">
                                {profile.awareness?.planDetails?.planDuration ||
                                  profile.planDuration ||
                                  profile.duration ||
                                  profile.awareness?.planDetails?.duration ||
                                  "N/A"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Full Name:</span>
                              <p className="text-sm">
                                {profile.awareness?.personalDetails?.name || profile.fullName || row.name || "N/A"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Mobile:</span>
                              <p className="text-sm">
                                {profile.awareness?.personalDetails?.phone || profile.mobile || row.mobile || "N/A"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Email:</span>
                              <p className="text-sm truncate">
                                {profile.awareness?.personalDetails?.email || profile.email || row.email || "N/A"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">PAN:</span>
                              <p className="text-sm">
                                {profile.awareness?.personalDetails?.pan || "N/A"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Gender:</span>
                              <p className="text-sm">
                                {profile.awareness?.personalDetails?.gender || "N/A"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">DOB:</span>
                              <p className="text-sm">
                                {profile.awareness?.personalDetails?.dob
                                  ? clientsStore.setFormattedDate(profile.awareness.personalDetails.dob)
                                  : "N/A"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Occupation:</span>
                              <p className="text-sm">
                                {profile.awareness?.personalDetails?.occupation || "N/A"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Plan Status:</span>
                              <p className="text-sm">
                                <span className={`px-2 py-1 rounded text-xs ${profile.planStatus === "Completed"
                                  ? "bg-green-100 text-green-800"
                                  : profile.planStatus === "Pending"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                  }`}>
                                  {profile.planStatus || "N/A"}
                                </span>
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Awaken Status:</span>
                              <p className="text-sm">
                                <span className={`px-2 py-1 rounded text-xs ${profile.awakenStatus === "Awaken Export Ready"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                                  }`}>
                                  {profile.awakenStatus || "N/A"}
                                </span>
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Created Date:</span>
                              <p className="text-sm">
                                {profile.createdDate ? clientsStore.setFormattedDate(profile.createdDate) : "N/A"}
                              </p>
                            </div>
                          </div>
                          {profile.uId && profile.uId.startsWith("MS") && (
                            <div className="mt-3 pt-3 border-t">
                              <button
                                onClick={async () => {
                                  // Check if UID is already used
                                  const existingUIds = await checkUsedUIds([profile.uId]);
                                  if (existingUIds.has(profile.uId)) {
                                    alert(`A form with UID ${profile.uId} already exists. Cannot create duplicate form.`);
                                    return;
                                  }

                                  // Set selected data and open modal
                                  setSelectedClientData({ row, profile });

                                  // Pre-select agent based on user role
                                  const currentUser = clientsStore?.user;
                                  const isFinancialPlanner = currentUser?.role === rolesWithId.agent;
                                  if (isFinancialPlanner && currentUser?._id) {
                                    setSelectedAgentId(currentUser._id);
                                  } else {
                                    setSelectedAgentId("");
                                  }

                                  setShowCreateFormModal(true);
                                }}
                                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                                disabled={usedUIds.has(profile.uId)}
                              >
                                {usedUIds.has(profile.uId) ? "Form Already Created" : "Create Awaken Form"}
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      });
    }

    // Use currentPageData which contains only the paginated data from API
    const dataToRender = currentPageData || [];

    // Apply simple client-side search across all plan tabs (except Fact Finder)
    const searchTerm = planSearch.trim().toLowerCase();
    const filteredData =
      searchTerm === ""
        ? dataToRender
        : dataToRender.filter((row) => {
            const personal = row.awareness?.personalDetails || row.personalDetails || {};
            const plan = row.awareness?.planDetails || row.planDetails || {};

            const fields = [
              personal.name,
              personal.fullName,
              row.name,
              row.fullName,
              personal.email,
              row.email,
              String(personal.phone || personal.mobile || personal.mobileNumber || row.phone || row.mobile || ""),
              plan.planType,
              row.planType,
              String(plan.planYear || row.planYear || ""),
              plan.planDuration,
              row.planDuration,
              row.factFinderId,
              row.factFinderUId,
              row.uId,
              row.uid,
              row._id,
            ];

            return fields.some(
              (value) =>
                typeof value === "string" &&
                value.toLowerCase().includes(searchTerm)
            );
          });

    if (filteredData.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="text-center py-4 text-gray-500">
            No Data Found
          </td>
        </tr>
      );
    }
    return filteredData.map((row, index) => {
      const globalIndex = currentPage * itemsPerPage + index + 1;
      return (
        <tr key={row._id || index} className="border-b">
          <td className=" text-center py-2 w-20 truncate">{globalIndex} .</td>
          <td className="text-left pl-8 py-2 w-20 truncate">
            {row.awareness?.personalDetails?.name || row.name}
          </td>
          <td className="text-left pl-8 py-2 w-20 truncate">
            <select
              id="assignAgent"
              onChange={async (e) => {
                const selectedAgentId = e.target.value;
                const currentAgentId = row?.assignedTo?._id;

                // If there's a current assignment and user is changing to a different agent
                if (
                  currentAgentId &&
                  selectedAgentId &&
                  selectedAgentId !== currentAgentId
                ) {
                  setReassignAgent({
                    newAgentId: selectedAgentId,
                    formId: row._id,
                    previousAgentId: currentAgentId,
                    formData: buildReassignSnapshot(row),
                  });
                  setShowReassignConfirm(true);
                } else if (!currentAgentId && selectedAgentId) {
                  // New assignment
                  setAssignAgent({
                    agentId: selectedAgentId,
                    formId: row._id,
                  });
                } else if (!selectedAgentId) {
                  // If no agent selected, refresh form data to show current state
                  await refreshFormData();
                }
              }}
              value={
                reassignAgent.formId === row._id
                  ? reassignAgent.newAgentId
                  : assignAgent.formId === row._id
                    ? assignAgent.agentId
                    : row?.assignedTo?._id || (clientsStore?.user?.role === rolesWithId.agent ? clientsStore.user._id : "")
              }
              className="w-40 py-1 rounded-lg border border-gray-400 focus:border-secondary outline-none"
              disabled={clientsStore?.user?.role === rolesWithId.agent}
            >
              <option value="">Select Agent</option>
              {Array.isArray(clientsStore.agents) &&
                clientsStore.agents.map((agent) => (
                  <option key={agent._id} value={agent._id}>
                    {agent.name}
                  </option>
                ))}
            </select>
          </td>
          <td className=" text-center py-2 w-20 truncate">
            {clientsStore.setFormattedDate(row.createdAt)}
          </td>
          <td className=" text-center py-2 w-20 truncate">
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              getRenewalStatus(row) === "Original"
                ? "bg-gray-100 text-gray-700"
                : "bg-purple-100 text-purple-700"
            }`}>
              {getRenewalStatus(row)}
            </span>
          </td>
          <td className=" text-center py-2  w-20 truncate text-sm">
            <ActionButton
              status={row.status}
              row={row}
              action={async () => {
                // If status is 0, this is an Assignment action
                if (row.status === 0) {
                  if (
                    assignAgent.agentId === "" ||
                    assignAgent.formId !== row._id
                  ) {
                    alert("Please select an agent to assign");
                    return;
                  }
                  const body = {
                    formId: row._id,
                    agentId: assignAgent.agentId,
                  };
                  setLoad(true);
                  const apiResponse = await apiPostPut(
                    body,
                    "/adminPanel/assign-client",
                    "PUT"
                  );
                  if (apiResponse.status === 200) {
                    // Send notification email to the assigned financial planner
                    await sendAssignmentNotification(assignAgent.agentId, row);

                    // Refresh data to update counts and lists
                    await refreshData();

                    // Also refresh form data to show updated assignment
                    await refreshFormData();

                    setAssignAgent({
                      agentId: "",
                      formId: "",
                    });
                    setLoad(false);
                    alert("Client assigned successfully");
                  } else {
                    setLoad(false);
                    alert("Failed to assign client");
                    console.error(
                      "Failed to assign client:",
                      apiResponse?.message
                    );
                  }
                } else {
                  // For other statuses, this action is called as a callback after status update
                  // So we just need to refresh the data
                  await refreshData();
                  await refreshFormData();
                }
              }}
            />
          </td>
        </tr>
      );
    });
  };

  // Helper function to format renewal status display
  // Backend provides: isRenewalForm (boolean), renewalCount (number | null)
  const getRenewalStatus = (row) => {
    // Check if this is a renewal form (backend provides isRenewalForm field)
    const isRenewal = row.isRenewalForm === true;
    
    // Get renewal count from backend (calculated based on plan year progression)
    // Backend returns: null for original forms, 1/2/3/etc. for renewals
    const renewalCount = row.renewalCount;
    
    // If it's a renewal and has a valid count, display R-{count}
    if (isRenewal && typeof renewalCount === 'number' && renewalCount > 0) {
      return `R-${renewalCount}`;
    } else if (isRenewal) {
      // Fallback: if renewal flag is true but count is missing/null, show "Renewal"
      return "Renewal";
    }
    
    // Original form (isRenewalForm is false or not set)
    return "Original";
  };

  const renderTableHeaders = () => {
    if (tab === 1) {
      return (
        // <div className="w-full">
        <tr className="text-blue-800 text-left whitespace-nowrap ">
          <th className=" text-center py-2  w-20 truncate">S.no</th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Client
          </th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Financial Planner
          </th>
          <th className=" text-center py-2  w-20 truncate">Created on</th>
          <th className=" text-center py-2  w-20 truncate">Renewal Status</th>
          <th className=" text-center py-2  w-20 truncate">Action</th>
        </tr>
        // </div>
      );
    }
    if (tab === 2) {
      return (
        // <div className="w-full">
        <tr className="text-blue-800 text-sm text-left  whitespace-nowrap">
          <th className=" text-center py-2  w-20 truncate">S.no</th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Client
          </th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Financial Planner
          </th>
          <th className=" text-center py-2  w-20 truncate">Created on</th>
          <th className=" text-center py-2  w-20 truncate">Renewal Status</th>
          <th className=" text-center py-2  w-20 truncate">Action</th>
        </tr>
        // </div>
      );
    }
    if (tab === 3) {
      return (
        <tr className="text-blue-800 text-sm text-left whitespace-nowrap">
          <th className=" text-center py-2  w-20 truncate">S.no</th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Client
          </th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Financial Planner
          </th>
          <th className=" text-center py-2  w-20 truncate">Created on</th>
          <th className="text-center py-2  w-20 truncate ">Status</th>
        </tr>
      );
    }
    if (tab === 4) {
      return (
        <tr className="text-blue-800 text-sm text-left  whitespace-nowrap">
          <th className=" text-center py-2  w-20 truncate">S.no</th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Client
          </th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Financial Planner
          </th>
          <th className=" text-center py-2  w-20 truncate">Created on</th>
          <th className=" text-center py-2  w-20 truncate">Renewal Status</th>
          <th className="text-center py-2  w-20 truncate ">Status</th>
        </tr>
      );
    }
    if (tab === 5) {
      return (
        <tr className="text-blue-800 text-sm text-left  whitespace-nowrap">
          <th className=" text-center py-2  w-20 truncate">S.no</th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Client
          </th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Financial Planner
          </th>
          <th className=" text-center py-2  w-20 truncate">Created on</th>
          <th className=" text-center py-2  w-20 truncate">Renewal Status</th>
          <th className="text-center py-2  w-20 truncate ">Status</th>
        </tr>
      );
    }
    if (tab === 6) {
      return (
        <tr className="text-blue-800 text-sm text-left whitespace-nowrap">
          <th className=" text-center py-2  w-20 truncate">S.no</th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Client
          </th>
          <th className=" text-left pl-8 py-2  w-20 truncate">
            Name of the Financial Planner
          </th>
          <th className=" text-center py-2  w-20 truncate">Created on</th>
          <th className="text-center py-2  w-20 truncate ">Status</th>
        </tr>
      );
    }
    if (tab === 7) {
      return (
        <tr className="text-blue-800 text-sm text-left whitespace-nowrap">
          <th className="text-center py-2 w-20 truncate">S.no</th>
          <th className="text-left pl-8 py-2 w-20 truncate">
            Name of the Client
          </th>
          <th className="text-left pl-8 py-2 w-20 truncate">
            Advisor Name
          </th>
          <th className="text-center py-2 w-20 truncate">Created on</th>
          <th className="text-center py-2 w-20 truncate">Status</th>
          <th className="text-center py-2 w-20 truncate">Profiles</th>
        </tr>
      );
    }
    return null;
  };

  return (
    <div className=" relative w-full h-screen bg-white">
      <Loader loader={load} text={"Loading....."} />
      <div className="bg-white ">
        <button
          className="text-2xl hover:text-primary flex items-center font-semibold p-6"
          onClick={() => navigate("/dashboard")}
        >
          <FaArrowLeftLong className="" />
          <span className="mx-3">Back</span>
        </button>
        <img
          src={mainstreamLogo}
          alt="LOGO"
          className="absolute right-4 top-1 md:top-2 w-28 h-auto z-20"
        />
      </div>

      <div className=" relative w-full">
        <img
          src={homeBanner}
          alt="homeBanner"
          className="mt-0 h-[25vh] object-cover object-top w-full"
        />
        <div className="absolute inset-0 right-3 flex items-center justify-center pr-4">
          <p className=" text-white text-2xl font-bold">
            {planType ? `${clientsStore.camelToTitleCase(planType)} Awakens` : "Loading..."}
          </p>
        </div>
      </div>

      <div className="mx-10 p-5 pt-8 -mt-[8vh] z-10  bg-white shadow-xl rounded-lg flex flex-col relative">
        {/* Search input for Awaken plans (tabs 1–6) */}
        {tab !== 7 && (
          <div className="mb-4 flex items-center gap-2">
            <input
              type="text"
              placeholder="Search by client name, email, mobile, UID, or plan..."
              value={planSearch}
              onChange={(e) => {
                setPlanSearch(e.target.value);
                // Keep pagination consistent by jumping back to first page when starting a new search
                setCurrentPage(0);
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {planSearch && (
              <button
                onClick={() => {
                  setPlanSearch("");
                  setCurrentPage(0);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Search input for Fact Finder clients */}
        {tab === 7 && (
          <div className="mb-4 flex items-center gap-2">
            <input
              type="text"
              placeholder="Search clients by name, email, or mobile..."
              value={factFinderSearch}
              onChange={(e) => setFactFinderSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(0);
                  fetchFactFinderClients();
                }
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => {
                setCurrentPage(0);
                fetchFactFinderClients();
              }}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Search
            </button>
            {factFinderSearch && (
              <button
                onClick={() => {
                  setFactFinderSearch("");
                  setCurrentPage(0);
                  fetchFactFinderClients();
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        )}
        <ul className="border-b-2 pb-5">
          {/* {statuses.map((status) => (
            <button
              key={status}
              onClick={() => clientsStore.setCurrentStatus(status)}
              className={`mr-2 p-1 px-4 gap-3 border rounded-lg text-gray-500 ${
                clientsStore.currentStatus === status
                  ? "bg-blue-500 text-white"
                  : "bg-white"
              }`}
            >
              {status}
            </button>
          ))} */}
          {statuses1.map((status) => (
            // <button
            //   key={status.id}
            //   onClick={status.action}
            //   className={`mr-5 p-1 px-5 gap-3 border rounded-lg text-gray-500 text-xs ${
            //     tab === status.id ? "bg-blue-500 text-white" : "bg-white"
            //   }`}
            // >
            //   {status.name}
            // </button>

            <button
              key={`${status.id}-${status.count}`}
              onClick={() => {
                status.action();
                setCurrentPage(0);
              }}
              className={`mr-2 p-1 py-2 px-5 border border-gray-500 rounded-lg text-gray-500 text-xs ${tab === status.id
                ? "bg-blue-500 text-white font-bold border-none"
                : "bg-white"
                }`}
            >
              {status.name}
            </button>
          ))}
        </ul>
        <div className="overflow-hidden overflow-x-auto">
          <table className="w-full ">
            <thead className="">{renderTableHeaders()}</thead>
            <tbody className="text-center">{renderTableData()}</tbody>
          </table>
        </div>
        {((tab === 7 && factFinderTotalPages > 1) || (tab !== 7 && totalPages > 1)) && (
          <div className="mt-4 flex justify-center">
              <ReactPaginate
                nextLabel=">"
                previousLabel="<"
                onPageChange={handlePageChange}
                forcePage={currentPage}
                pageRangeDisplayed={2}
                marginPagesDisplayed={1}
                pageCount={tab === 7 ? factFinderTotalPages : totalPages}
                breakLabel="..."
                breakClassName="page-item"
                breakLinkClassName="page-link px-3 py-2"
                containerClassName="flex items-center gap-1"
                pageClassName="page-item"
                pageLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors"
                previousClassName="page-item"
                previousLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors"
                nextClassName="page-item"
                nextLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors"
                activeClassName="active"
                activeLinkClassName="bg-blue-500 text-white hover:bg-blue-600 font-semibold"
                disabledClassName="opacity-50 cursor-not-allowed"
                disabledLinkClassName="cursor-not-allowed"
                renderOnZeroPageCount={null}
              />
          </div>
        )}
      </div>

      {/* Create Awaken Form Modal */}
      {showCreateFormModal && selectedClientData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Create Awaken Form
            </h3>

            {/* Client Information */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-700 mb-2">Client Information</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>
                  <p className="font-medium">
                    {selectedClientData.profile.awareness?.personalDetails?.name ||
                      selectedClientData.profile.fullName ||
                      selectedClientData.row.name ||
                      "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">UID:</span>
                  <p className="font-mono font-semibold">{selectedClientData.profile.uId || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-600">Plan Type:</span>
                  <p className="font-medium">
                    {selectedClientData.profile.awareness?.planDetails?.planType ||
                      selectedClientData.profile.plan ||
                      "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Email:</span>
                  <p className="font-medium">
                    {selectedClientData.profile.awareness?.personalDetails?.email ||
                      selectedClientData.profile.email ||
                      selectedClientData.row.email ||
                      "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Financial Planner Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Financial Planner
              </label>
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                disabled={clientsStore?.user?.role === rolesWithId.agent}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select Financial Planner</option>
                {Array.isArray(clientsStore.agents) &&
                  clientsStore.agents.map((agent) => (
                    <option key={agent._id} value={agent._id}>
                      {agent.name || agent.email}
                    </option>
                  ))}
              </select>
              {clientsStore?.user?.role === rolesWithId.agent && (
                <p className="mt-1 text-xs text-gray-500">
                  You are automatically assigned as the financial planner
                </p>
              )}
            </div>

            {/* Warning if UID already used */}
            {usedUIds.has(selectedClientData.profile.uId) && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">
                  ⚠️ A form with UID <strong>{selectedClientData.profile.uId}</strong> already exists.
                  Cannot create duplicate form.
                </p>
              </div>
            )}

            {checkingUIds && (
              <div className="mb-4 flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600 text-sm">Checking for existing forms...</span>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateFormModal(false);
                  setSelectedClientData(null);
                  setSelectedAgentId("");
                }}
                className="px-4 py-2 rounded-lg text-gray-600 bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!selectedAgentId) {
                    alert("Please select a financial planner");
                    return;
                  }

                  if (usedUIds.has(selectedClientData.profile.uId)) {
                    alert(`A form with UID ${selectedClientData.profile.uId} already exists. Cannot create duplicate form.`);
                    return;
                  }

                  const currentUser = clientsStore?.user;
                  const profile = selectedClientData.profile;

                  // Store assignment intent before opening form
                  if (currentUser?._id) {
                    storeAssignmentIntent(profile.uId);
                  }

                  // Get the current timestamp when fetching data from Fact Finder
                  // This should be used as createdAt instead of Fact Finder's original date
                  const awakenCreatedAt = new Date().toISOString();

                  // Generate form link with selected agent and fetch timestamp
                  const userRoleForForm = currentUser?.role || null;
                  const formLink = adminAuthService.generateFactFinderFormLink(
                    profile.uId,
                    userRoleForForm,
                    selectedAgentId,
                    awakenCreatedAt
                  );

                  if (formLink) {
                    console.log(`Creating form: UID=${profile.uId}, AgentId=${selectedAgentId}`);
                    console.log(`Form URL: ${formLink}`);

                    // Close modal
                    setShowCreateFormModal(false);
                    setSelectedClientData(null);
                    setSelectedAgentId("");

                    // Mark UID as used
                    setUsedUIds(prev => new Set([...prev, profile.uId]));

                    // Open form in new tab
                    window.open(formLink, "_blank");

                    // Set up periodic check for newly assigned client (for both agents and superadmin)
                    console.log(`Will check for assigned client with UID ${profile.uId} in 5 seconds...`);
                    setTimeout(() => {
                      processPendingAssignments();
                      refreshData();
                      fetchClientsData(planType, statuses[tab]);
                    }, 5000);

                    const handleFocus = () => {
                      setTimeout(() => {
                        console.log("Window focused - checking for newly assigned client...");
                        processPendingAssignments();
                        refreshData();
                        fetchClientsData(planType, statuses[tab]);
                      }, 2000);
                      window.removeEventListener('focus', handleFocus);
                    };
                    window.addEventListener('focus', handleFocus);
                  } else {
                    alert("Authentication required. Please log in again.");
                  }
                }}
                disabled={!selectedAgentId || usedUIds.has(selectedClientData.profile.uId) || checkingUIds}
                className={`px-4 py-2 rounded-lg transition-colors ${!selectedAgentId || usedUIds.has(selectedClientData.profile.uId) || checkingUIds
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
              >
                Create Form
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassignment Confirmation Modal */}
      {showReassignConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirm Reassignment
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to reassign this client to a different
              Financial Planner? This will notify both the current and new
              Financial Planner via email.
            </p>
            {isReassigning && (
              <div className="mb-4 flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">
                  Reassigning client...
                </span>
              </div>
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={async () => {
                  if (!isReassigning) {
                    setShowReassignConfirm(false);
                    setReassignAgent({
                      newAgentId: "",
                      formId: "",
                      previousAgentId: "",
                      formData: null,
                    });
                    // Refresh form data when modal is closed without action
                    await refreshFormData();
                  }
                }}
                disabled={isReassigning}
                className={`px-4 py-2 rounded-lg transition-colors ${isReassigning
                  ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                  : "text-gray-600 bg-gray-200 hover:bg-gray-300"
                  }`}
              >
                Cancel
              </button>
              <button
                onClick={handleReassignment}
                disabled={isReassigning}
                className={`px-4 py-2 rounded-lg transition-colors ${isReassigning
                  ? "bg-blue-400 text-white cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
              >
                {isReassigning ? "Reassigning..." : "Confirm Reassignment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ClientsList;
